import { useEffect, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { fetchPlans, type PublicPlan } from '@/features/subscription/plans-api';

/**
 * useSubscriptionPlans — Batch 11 Commit 2.
 *
 * Caches the public plan catalog (`GET /api/subscriptions/plans`,
 * Backend Commit 0) at module scope so A-02 Browse-Mode + Pakete
 * Section share a single fetch across the session. Pattern mirrors
 * `useSubscriptionState` / `useEntitlement` exactly: module-level
 * `cache` + `inflight` promise dedup + `clearSubscriptionPlansCache()`
 * for logout / test isolation.
 *
 * Plans rarely change (catalog updates are admin-only and ship as
 * code-deploys today), so a session-long cache is fine. If a future
 * Phase makes the catalog admin-editable at runtime, swap to a TTL
 * or invalidate on a webhook event.
 */

let cache: PublicPlan[] | null = null;
let inflight: Promise<PublicPlan[]> | null = null;

/**
 * Test-only / logout-flow cache invalidation. Symmetric with the
 * companion clearer in `useSubscriptionState` / `useEntitlement`.
 */
export function clearSubscriptionPlansCache(): void {
  cache = null;
  inflight = null;
}

async function fetchPlansCached(): Promise<PublicPlan[]> {
  if (cache) return cache;
  if (inflight) return inflight;

  inflight = fetchPlans()
    .then((result) => {
      cache = result;
      return result;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

interface UseSubscriptionPlansResult {
  plans: PublicPlan[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSubscriptionPlans(): UseSubscriptionPlansResult {
  const [plans, setPlans] = useState<PublicPlan[] | null>(cache);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!cache);

  useEffect(() => {
    let cancelled = false;
    void fetchPlansCached()
      .then((result) => {
        if (cancelled) return;
        setPlans(result);
        setError(null);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(err.envelope?.user_message ?? `HTTP ${err.httpStatus}`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Plan-Katalog konnte nicht geladen werden.');
        }
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = async (): Promise<void> => {
    clearSubscriptionPlansCache();
    setIsLoading(true);
    try {
      const result = await fetchPlansCached();
      setPlans(result);
      setError(null);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(err.envelope?.user_message ?? `HTTP ${err.httpStatus}`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Plan-Katalog konnte nicht geladen werden.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { plans, isLoading, error, refetch };
}
